<script>
import { toDecPercent, toMoney, formatNumber } from '@utils/Format'
import { aawzDate } from '@utils/dates/aawz-date'
import FeedbackWrapper from '@modules/global/components/feedback-wrapper/feedback-wrapper.vue'
import {
  GROUP_EMPTY_CELL,
  GROUP_TOTAL_EXPLAINER_LINK,
  GROUP_TOTAL_NOTE,
  GROUP_TOTAL_ROW_LABEL,
  GROUP_TOTAL_ROW_LABEL_FILTERED,
  GROUP_WALLET_CELL_TYPE,
  GROUP_WALLET_COLUMNS,
} from '@modules/consolidator/constants/group-wallet'

const RELATIVE_PRECISION = 0
/**
 * O motor devolve `statistics.data_inicio` como `DD/MM/YYYY` (é o formato do
 * `portfolio_analisys_consolidation`); o ISO fica para payloads que vêm
 * direto do banco. Tentar só o ISO fazia `aawzDate` devolver data inválida e a
 * coluna "Início" renderizava "-" em TODAS as linhas — justamente a coluna que
 * revela que o grupo cobre uma janela maior que a de um membro que entrou
 * depois, e que explica a rentabilidade do Total.
 */
const DATE_INPUT_FORMATS = ['DD/MM/YYYY', 'YYYY-MM-DD']
const DATE_OUTPUT_FORMAT = 'DD/MM/YYYY'

/**
 * Seção "Por Carteira": uma linha por membro do grupo e uma linha Total.
 *
 * ★ Componente de apresentação puro. Cada número exibido aqui já veio calculado
 * do backend — as linhas de membro vêm do `breakdown` (group_by=id_cliente) e a
 * linha Total vem do `portfolio` do grupo inteiro. Nenhuma célula é somada,
 * promediada ou ponderada no cliente: rentabilidade e volatilidade são razões,
 * e razões não somam nem promediam.
 *
 * A explicação do total é UMA e vive no ícone ao lado do título do card, não
 * repetida célula a célula (DS-EXPLICACAO-NO-TITULO).
 *
 * ★ SELEÇÃO POR LINHA — REMOVIDA A PEDIDO (2026-08-04), NÃO É ESQUECIMENTO.
 * A linha era clicável e filtrava a carteira para aquele membro. A usabilidade
 * ficou ruim: quase ninguém descobria, e quem descobria filtrava sem querer.
 * Para restaurar bastam quatro peças, todas nesta ordem:
 *   1. no `<tr>` de `displayRows`: `role="button"`, `tabindex="0"`,
 *      `:aria-label` (o `ariaLabel` saiu do builder de linha junto, era
 *      `Filtrar a carteira do grupo por ${nome}`), `@click`/`@keydown.enter`/
 *      `@keydown.space.prevent` chamando `selectMember(row)`;
 *   2. método `selectMember(row)` emitindo
 *      `$emit('select-member', { id: row.id, nome: row.nome })`;
 *   3. estado visual: `.group-wallet-table__row` com `cursor: pointer`, hover e
 *      modificador `--selected` casando `filteredMemberName === row.nome`;
 *   4. no consumidor: `@select-member` nas 3 sub-abas repassando pro shell e
 *      o `handleSelectMember` no shell (saíram junto, eram acoplamento morto).
 *      A prop `filteredMemberName` e o botão "Ver o grupo completo" continuam
 *      no lugar.
 * Se voltar, `DS-LINHA-CLICAVEL-DESCOBRIVEL` exige a affordance junto.
 */
export default {
  name: 'GroupWalletTable',
  components: { FeedbackWrapper },
  props: {
    rows: {
      type: Array,
      default: () => [],
    },
    totalRow: {
      type: Object,
      default: null,
    },
    columns: {
      type: Array,
      default: () => GROUP_WALLET_COLUMNS,
      validator: (value) =>
        value.every((column) => !!column && !!column.key && !!column.label),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: 'Por Carteira',
    },
    benchmarkLabel: {
      type: String,
      default: '',
    },
    filteredMemberName: {
      type: String,
      default: '',
    },
    currency: {
      type: String,
      default: 'real',
    },
    totalMembersCount: {
      type: Number,
      default: 0,
    },
    // O vazio tem mais de uma causa: pode não haver membro no recorte, ou o
    // "Agrupar por" pode estar em outra dimensão que não a de membro. Quem sabe
    // qual é o caso é a view; a tabela só exibe.
    emptyMessage: {
      type: String,
      default: 'Nenhum membro com carteira no recorte selecionado.',
    },
  },
  data() {
    return {
      note: GROUP_TOTAL_NOTE,
      explainerLink: GROUP_TOTAL_EXPLAINER_LINK,
    }
  },
  computed: {
    notas() {
      return [this.filteredFooter, this.note, this.windowNote].filter(Boolean)
    },
    isFiltered() {
      return this.filteredMemberName.length > 0
    },
    filteredLabel() {
      return `Filtrado: ${this.filteredMemberName}`
    },
    filteredFooter() {
      if (!this.isFiltered || !this.totalMembersCount) return ''
      return `Exibindo ${this.rows.length} de ${this.totalMembersCount} membros.`
    },
    isEmpty() {
      return !this.loading && this.rows.length === 0
    },
    /**
     * Aviso de janela: o Total do grupo cobre desde a estreia do membro MAIS
     * ANTIGO, então ele pode ficar acima do maior membro sem que nada esteja
     * errado — o período do grupo simplesmente é maior. É o que faz o consultor
     * ler o número como se estivesse quebrado.
     *
     * Só compara datas já renderizadas nas próprias linhas; nenhuma métrica é
     * recalculada aqui.
     */
    windowNote() {
      if (this.isFiltered || !this.totalRow) return ''
      const inicioTotal = this.parseDate(this.totalRow.inicio)
      if (!inicioTotal) return ''
      const posteriores = this.membersStartingAfter(inicioTotal)
      if (posteriores.length === 0) return ''
      const nomes = posteriores.join(', ')
      return (
        `O Total do grupo é medido desde ${this.formatDate(
          this.totalRow.inicio
        )}, ` +
        `antes da entrada de ${nomes}. Por cobrir um período maior, ele pode ficar ` +
        `acima ou abaixo de todos os membros.`
      )
    },
    displayColumns() {
      return this.columns.map((column) => ({
        key: column.key,
        label: column.label,
        className: this.resolveAlignClass(column),
      }))
    },
    displayRows() {
      return this.rows.map((row) => ({
        id: row.id,
        nome: row.nome,
        color: row.color,
        cells: this.buildCells(row),
      }))
    },
    displayTotalRow() {
      if (!this.totalRow) return null
      const label = this.isFiltered
        ? GROUP_TOTAL_ROW_LABEL_FILTERED
        : GROUP_TOTAL_ROW_LABEL
      return { cells: this.buildCells({ ...this.totalRow, nome: label }, true) }
    },
  },
  methods: {
    buildCells(row, isTotal = false) {
      return this.columns.map((column) => ({
        key: column.key,
        className: this.resolveAlignClass(column),
        ...this.formatCell(row, column),
      }))
    },
    formatCell(row, column) {
      if (column.cellType === GROUP_WALLET_CELL_TYPE.MEMBER) {
        return { text: row.nome, secondary: row.parentesco || '' }
      }

      const value = row[column.key]
      if (value === null || value === undefined) {
        return { text: GROUP_EMPTY_CELL, secondary: '' }
      }

      return {
        text: this.formatValue(value, column.cellType),
        secondary: this.resolveSecondary(row, column),
      }
    },
    formatValue(value, cellType) {
      if (cellType === GROUP_WALLET_CELL_TYPE.CURRENCY) {
        return toMoney(value, 2, this.currency)
      }
      if (cellType === GROUP_WALLET_CELL_TYPE.PERCENT)
        return toDecPercent(value)
      if (cellType === GROUP_WALLET_CELL_TYPE.RELATIVE) {
        return this.formatRelative(value)
      }
      if (cellType === GROUP_WALLET_CELL_TYPE.DECIMAL)
        return formatNumber(value)
      if (cellType === GROUP_WALLET_CELL_TYPE.DATE)
        return this.formatDate(value)
      return String(value)
    },
    // O relativo é um múltiplo do benchmark (0,45 = 45% do CDI). O rótulo do
    // benchmark DO GRUPO acompanha o número porque ele pode divergir do
    // benchmark que o membro usa na tela individual.
    formatRelative(value) {
      const percent = toDecPercent(value, RELATIVE_PRECISION)
      return this.benchmarkLabel ? `${percent} ${this.benchmarkLabel}` : percent
    },
    // `aawzDate` não aceita lista de formatos (diferente do moment), então a
    // tentativa é manual — ver @utils/dates/aawz-date.
    // O guard de vazio é obrigatório: `aawzDate(null, formato)` devolve HOJE,
    // não data inválida — sem ele, membro sem data de início entraria na
    // comparação de janela como se tivesse começado hoje.
    parseDate(value) {
      if (!value) return null
      for (const formato of DATE_INPUT_FORMATS) {
        const date = aawzDate(value, formato)
        if (date.isValid()) return date
      }
      return null
    },
    formatDate(value) {
      const date = this.parseDate(value)
      if (!date) return GROUP_EMPTY_CELL
      return date.format(DATE_OUTPUT_FORMAT)
    },
    // Nomes dos membros cuja carteira começa depois do início do grupo.
    membersStartingAfter(inicioTotal) {
      const limite = inicioTotal.valueOf()
      const nomes = []
      for (const row of this.rows) {
        const inicio = this.parseDate(row.inicio)
        if (inicio && inicio.valueOf() > limite) nomes.push(row.nome)
      }
      return nomes
    },
    // Participação do membro no PL do grupo, exibida sob o valor em reais.
    resolveSecondary(row, column) {
      const isCurrency = column.cellType === GROUP_WALLET_CELL_TYPE.CURRENCY
      const percent = row.plPercent
      if (!isCurrency || percent === null || percent === undefined) return ''
      return toDecPercent(percent)
    },
    resolveAlignClass(column) {
      return column.align === 'left' ? 'text-left' : 'text-right'
    },
    clearFilter() {
      this.$emit('clear-filter')
    },
    explainTotal() {
      this.$emit('explain-total')
    },
  },
}
</script>

<template>
  <div
    class="group-wallet-table"
    :class="{ 'az-card-bordered': !loading }"
  >
    <div v-if="!loading" class="d-flex align-center flex-wrap mb-4">
      <span class="az-ds-card-title">{{ title }}</span>
      <v-tooltip right max-width="360">
        <template v-slot:activator="{ on, attrs }">
          <button
            type="button"
            class="group-wallet-table__explain ml-2"
            :aria-label="explainerLink"
            v-bind="attrs"
            @click="explainTotal"
            v-on="on"
          >
            <icon-svg name="mdi-information" :size="16" color="#8E8E8E" />
          </button>
        </template>
        <span>{{ explainerLink }}</span>
      </v-tooltip>
      <v-spacer />
      <div v-if="isFiltered" class="d-flex align-center">
        <span class="az-text-secondary mr-2">{{ filteredLabel }}</span>
        <BaseButton underlined fitContent @click="clearFilter">
          Ver o grupo completo
        </BaseButton>
      </div>
    </div>

    <FeedbackWrapper
      :loading="loading"
      :noData="isEmpty"
      :message="emptyMessage"
      :minHeight="280"
    >
      <div class="group-wallet-table__scroll table-new-ux-v1">
        <table class="group-wallet-table__table">
          <thead>
            <tr>
              <th
                v-for="column in displayColumns"
                :key="column.key"
                scope="col"
                :class="column.className"
              >
                {{ column.label }}
              </th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="row in displayRows"
              :key="row.id"
            >
              <td
                v-for="cell in row.cells"
                :key="cell.key"
                :class="cell.className"
              >
                <div class="group-wallet-table__value">
                  <span
                    v-if="cell.key === 'membro'"
                    class="group-wallet-table__dot"
                    :style="{ backgroundColor: row.color }"
                  />
                  <span>{{ cell.text }}</span>
                </div>
                <div v-if="cell.secondary" class="group-wallet-table__sub">
                  {{ cell.secondary }}
                </div>
              </td>
            </tr>
          </tbody>

          <tfoot v-if="displayTotalRow">
            <tr class="group-wallet-table__total">
              <th
                v-for="cell in displayTotalRow.cells"
                :key="cell.key"
                scope="row"
                :class="cell.className"
              >
                <div class="group-wallet-table__value">
                  <span>{{ cell.text }}</span>
                </div>
              </th>
            </tr>
          </tfoot>
        </table>
      </div>

      <div
        v-for="(nota, indice) in notas"
        :key="indice"
        class="az-info-note mt-3"
      >
        <icon-svg name="info-circle" :size="12" class="flex-shrink-0" />
        <span>{{ nota }}</span>
      </div>
    </FeedbackWrapper>
  </div>
</template>

<style lang="sass" scoped>
.group-wallet-table
  height: 100%
  &__scroll
    width: 100%
    overflow-x: auto

  &__table
    width: 100%
    min-width: 720px
    border-collapse: collapse

    th, td
      padding: 12px 8px
      white-space: nowrap

    // Sem font-size/color aqui: quem manda e o .table-new-ux-v1 (11px
    // uppercase). Sobrescrever no escopo do componente foi o que deixou o
    // header maior que o das tabelas irmas.
    thead th
      border-bottom: 1px solid var(--gray-3)

    // O wrapper so alcanca `tbody td`; as celulas em <th> (nome do membro e
    // linha de total) ficavam no tamanho herdado da pagina.
    tbody th,
    tfoot th
      font-size: 12px
      line-height: 20px

    tbody td
      border-bottom: 1px solid var(--gray-2)

  &__sub
    font-size: 11px
    line-height: 16px
    color: var(--secondary)

  &__value
    display: flex
    align-items: center
    gap: 6px

  .text-right &__value
    justify-content: flex-end

  &__total th
    border-top: 2px solid var(--gray-3)
    font-weight: var(--font-weight-semibold)

  &__dot
    width: 10px
    height: 10px
    border-radius: 50%
    flex: 0 0 auto

  &__info
    color: var(--secondary)
    line-height: 0
</style>
